import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { ProjectItemCategoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsProjectsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectItemTemplateCategoryActions from "../../../actions/projectItemTemplateCategory";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { ProjectLookupsLayout } from "../../index";

import "./ProjectItemTemplateCategorysPage.scss";

interface ProjectItemTemplateCategorysPageProps {
    formFilter: IFormFilter;
    projectItemTemplateCategory: Dtos.ProjectItemTemplateCategory[];
    loadingProjectItemTemplateCategorys: boolean;
    loadProjectItemTemplateCategorysSuccess: boolean;
    loadProjectItemTemplateCategorysFailure: boolean;
    loadProjectItemTemplateCategorysErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearProjectItemTemplateCategorys: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategoryClearActionCreator;
    loadProjectItemTemplateCategorys: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategoryLoadActionCreator;
    filter: ProjectItemTemplateCategoryActions.IProjectItemTemplateCategoryFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const ProjectItemTemplateCategorysPage: FC<
    ProjectItemTemplateCategorysPageProps
> = ({
    formFilter,
    projectItemTemplateCategory,
    loadingProjectItemTemplateCategorys,
    loadProjectItemTemplateCategorysSuccess,
    loadProjectItemTemplateCategorysFailure,
    loadProjectItemTemplateCategorysErrorCode,
    permissions,
    clearProjectItemTemplateCategorys,
    loadProjectItemTemplateCategorys,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showProjectItemTemplateCategorys =
        !loadingProjectItemTemplateCategorys &&
        loadProjectItemTemplateCategorysSuccess;


    // Load project item template categories
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsProjectsSubMenu(permissions));
            setBreadcrumbs(ProjectItemCategoriesBreadcrumbs());

            loadProjectItemTemplateCategorys();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsProjectsSubMenu,
        permissions,
        setBreadcrumbs,
        ProjectItemCategoriesBreadcrumbs,
        loadProjectItemTemplateCategorys,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectItemTemplateCategorysFailure) {
            navigate(`/error/${loadProjectItemTemplateCategorysErrorCode}`);
        }
    }, [
        loadProjectItemTemplateCategorysFailure,
        loadProjectItemTemplateCategorysErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the project item template categories on component unmount
    useEffect(() => {
        return () => {
            clearProjectItemTemplateCategorys();
        };
    }, [clearProjectItemTemplateCategorys]);

    const createTable = useMemo(() => {
        if (
            !projectItemTemplateCategory ||
            projectItemTemplateCategory.length == 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no item categories to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            projectItemTemplateCategory.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={projectItemTemplateCategory}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : projectItemTemplateCategory.length
                    }
                    pageSize={
                        showPagination
                            ? undefined
                            : projectItemTemplateCategory.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no item categories to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/project/itemcategories/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        projectItemTemplateCategory,
        DEFAULT_PAGE_SIZE,
        PAGE_SIZE_OPTIONS,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Project Item Categories</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ProjectItemCategoriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/project/itemcategories/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showProjectItemTemplateCategorys ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showProjectItemTemplateCategorys ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showProjectItemTemplateCategorys,
        createTable,
    ]);

    return (
        <ProjectLookupsLayout
            loading={loadingProjectItemTemplateCategorys}
            permission={Dtos.Permission.ProjectItemCategoriesView}
        >
            {renderContent}
        </ProjectLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ProjectItemTemplateCategorysPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    return {
        formFilter: state.projectItemTemplateCategorys.formFilter,
        projectItemTemplateCategory:
            state.projectItemTemplateCategorys.formFilteredData,
        loadingProjectItemTemplateCategorys:
            state.projectItemTemplateCategorys.loadState &&
            state.projectItemTemplateCategorys.loadState.status ==
                RequestState.Pending,
        loadProjectItemTemplateCategorysSuccess:
            state.projectItemTemplateCategorys.loadState &&
            state.projectItemTemplateCategorys.loadState.status ==
                RequestState.Success,
        loadProjectItemTemplateCategorysFailure:
            state.projectItemTemplateCategorys.loadState &&
            state.projectItemTemplateCategorys.loadState.status ==
                RequestState.Failure,
        loadProjectItemTemplateCategorysErrorCode:
            state.projectItemTemplateCategorys.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ProjectItemTemplateCategorysPageProps> => {
    return {
        loadProjectItemTemplateCategorys: bindActionCreators(
            ProjectItemTemplateCategoryActions.LoadProjectItemTemplateCategory,
            dispatch
        ),
        clearProjectItemTemplateCategorys: bindActionCreators(
            ProjectItemTemplateCategoryActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            ProjectItemTemplateCategoryActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProjectItemTemplateCategorysPage);
